import {GridColDef} from '@mui/x-data-grid-pro';
import _ from 'lodash';
import {getFileSizeWithUnit} from '../../../utils/utils';

type CustomGridColDef = GridColDef & {
  dataType?: string;
  UIDataType?: string;
  headerFilter?: string;
  isInfoIcon?: boolean;
  childHeaderName?: string;
  showInfo?: boolean;
  showInfoTooltipOnClick?: boolean;
  infoMessage?: string;
  label?: string;
  showHeaderTooltip?: boolean;
};

const safeIdInfo = 'evidenceSource.tooltip.safeId';
const clientRefInfo = 'evidenceSource.tooltip.clientReference';
const processingDatasetInfo = 'evidenceSource.tooltip.processingDataset';
const viewMoreInfoText = 'evidenceSource.tooltip.viewMoreInfo';

export const columnsConfiguration: CustomGridColDef[] = [
  {
    field: 'dateTime',
    headerName: 'dateTime',
    sortable: true,
    resizable: true,
    minWidth: 160,
    dataType: 'date',
    UIDataType: 'text',
    headerFilter: 'date',
    type: 'string',
  },
  {
    field: 'evidenceSource',
    headerName: 'evidenceSource',
    sortable: true,
    resizable: true,
    minWidth: 180,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: 'search',
    type: 'string',
  },
  {
    field: 'safeID',
    headerName: 'safeID',
    sortable: false,
    resizable: true,
    minWidth: 155,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: '',
    type: 'string',
    showInfo: true,
    showInfoTooltipOnClick: true,
    infoMessage: safeIdInfo,
    label: viewMoreInfoText,
  },
  {
    field: 'originatingUser',
    headerName: 'originatingUser',
    sortable: true,
    resizable: true,
    minWidth: 170,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: 'filter',
    type: 'string',
  },
  {
    field: 'receiptMethod',
    headerName: 'receiptMethod',
    sortable: true,
    resizable: true,
    minWidth: 180,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: 'filter',
    type: 'string',
  },
  {
    field: 'custodianType',
    headerName: 'custodianType',
    sortable: false,
    resizable: true,
    minWidth: 150,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: '',
    type: 'string',
    childHeaderName: 'topLevelPath',
  },
  {
    field: 'custodianAssigned',
    headerName: 'custodianAssigned',
    sortable: true,
    resizable: true,
    minWidth: 200,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: 'filter',
    type: 'string',
  },
  {
    field: 'preProcessedSize',
    headerName: 'preProcessedSize',
    sortable: false,
    resizable: true,
    minWidth: 140,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: '',
    type: 'string',
  },
  {
    field: 'mappedDataSize',
    headerName: 'mappedDataSize',
    sortable: false,
    resizable: true,
    minWidth: 140,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: '',
    type: 'string',
  },
  {
    field: 'clientRef',
    headerName: 'clientRef',
    sortable: true,
    resizable: true,
    minWidth: 160,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: 'search',
    type: 'string',
    showInfo: true,
    showInfoTooltipOnClick: true,
    infoMessage: clientRefInfo,
    label: viewMoreInfoText,
  },
  {
    field: 'status',
    headerName: 'status',
    sortable: true,
    resizable: true,
    minWidth: 180,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: 'filter',
    type: 'string',
  },
  {
    field: 'processingDatasetID',
    headerName: 'processingDatasetID',
    sortable: false,
    resizable: true,
    minWidth: 150,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: '',
    type: 'string',
    showInfo: true,
    showInfoTooltipOnClick: true,
    infoMessage: processingDatasetInfo,
    label: viewMoreInfoText,
  },
  {
    field: 'deDupeMethod',
    headerName: 'deDupeMethod',
    sortable: true,
    resizable: true,
    minWidth: 150,
    dataType: 'text',
    UIDataType: 'text',
    headerFilter: 'filter',
    type: 'string',
  },
];

export const mapEvidenceApiToGrid = (apiData: any[]) => {
  const getCustodianAssignedData = (item: any, level: 'PARENT' | 'CHILD') => {
    const custodianAssigned = [];
    if (item.custodianType === 'Single' && level === 'PARENT') {
      const mediaGroup = _.get(item, 'mediaGroups[0]', null);
      if (mediaGroup && _.get(mediaGroup, 'custId', null)) {
        custodianAssigned.push({
          id: `${_.get(mediaGroup, 'custId', 0)}`,
          name: _.get(mediaGroup, 'custodianName', '') as string,
        });
      }
    }
    if (level === 'CHILD' && _.get(item, 'custId', null)) {
      custodianAssigned.push({
        id: `${_.get(item, 'custId', 0)}`,
        name: _.get(item, 'custodianName', '') as string,
      });
    }
    return custodianAssigned;
  };

  const getValueForSingleCustodianType = (
    item: any,
    keyFromMedia: string
  ): string | number | null => {
    let returnValue: string | number = _.get(
      item,
      keyFromMedia,
      keyFromMedia === 'batchingGroup' ? null : ''
    );
    if (item.custodianType === 'Single') {
      const mediaGroup = _.get(item, 'mediaGroups[0]', null);
      if (mediaGroup && _.get(mediaGroup, keyFromMedia, null)) {
        returnValue = _.get(mediaGroup, keyFromMedia);
      }
    }
    return returnValue;
  };

  return apiData.map((item: any) => ({
    evidMediaId: item?.evidMediaId,
    id: item?.evidMediaId,
    evidenceSource: item?.evidenceSource,
    projectName: item?.evidenceSource,
    dateTime: item?.evidMediaDate,
    safeID: item?.safeId ?? '',
    originatingUser: item?.originatingUser ?? '',
    receiptMethod: item?.receiptMethod ?? '',
    custodianType: item?.custodianType ?? '',
    custodianAssigned: getCustodianAssignedData(item, 'PARENT'),
    preProcessedSize: !_.isNil(item?.preProcessingSize) ? getFileSizeWithUnit(item.preProcessingSize) : "",
    mappedDataSize: item?.mappedDataSize
      ? getFileSizeWithUnit(item.mappedDataSize)
      : '',
    clientRef: getValueForSingleCustodianType(
      item,
      'clientReferenceNumber'
    ) as string,
    status: getValueForSingleCustodianType(item, 'status') as string,
    processingDatasetID: item?.eFile ?? '',
    deDupeMethod: getValueForSingleCustodianType(
      item,
      'deDupeMethod'
    ) as string,
    mediaId: item?.mediaId,
    batchingGroup: getValueForSingleCustodianType(item, 'batchingGroup'),
    quadSRecord: item.quadSRecord ?? '',
    children:
      item?.mediaGroups?.map((child: any) => ({
        ...child,
        id: child?.id,
        evidenceSource: item?.evidenceSource,
        dateTime: child?.evidMediaDate ?? '',
        safeID: child?.safeId ?? '',
        originatingUser: child?.originatingUser ?? '',
        receiptMethod: child?.receiptMethod ?? '',
        custodianType: item?.custodianType ?? '',
        topLevelPath: child?.topLevelPath ?? '',
        custodianAssigned: getCustodianAssignedData(child, 'CHILD'),
        preProcessedSize: child?.preProcessingSize
          ? getFileSizeWithUnit(child.preProcessingSize)
          : '',
        mappedDataSize: child?.mappedDataSize
          ? getFileSizeWithUnit(child.mappedDataSize)
          : '',
        clientRef: child?.clientReferenceNumber ?? '',
        status: _.get(child, 'status', ''),
        processingDatasetID: child?.eFile ?? '',
        deDupeMethod: child?.deDupeMethod ?? '',
        batchingGroup: _.get(child, 'batchingGroup', null),
      })) ?? [],
  }));
};

export const sortFilterColumnFieldMap: Record<string, string> = {
  evidenceSource: 'evidenceSource',
  originatingUser: 'originatingUser',
  receiptMethod: 'receiptMethod',
  custodianAssigned: 'custodianName',
  clientRef: 'clientReferenceNumber',
  status: 'statuses',
  deDupeMethod: 'deDupeMethod',
  dateTime: 'evidMediaDate',
};

export const dateOperatorMap: Record<string, string> = {
  is: 'is',
  before: 'isBefore',
  onOrBefore: 'isORBefore',
  after: 'isAfter',
  onOrAfter: 'isOrAfter',
};
